// NPM packages
import Vue from 'vue';
import { Vuelidate } from 'vuelidate';
import Flicking from '@egjs/vue-flicking';
import VueTheMask from 'vue-the-mask';
import LottieAnimation from 'lottie-web-vue';
import VCalendar from 'v-calendar';
import VueEllipseProgress from 'vue-ellipse-progress';
import Router from 'vue-router';
import Vuetify from 'vuetify/lib/framework';
import Vuex from 'vuex';
import VueGtm from '@gtm-support/vue2-gtm';
import VueMeta from 'vue-meta';
import VueScrollTo from 'vue-scrollto';
import { createHead } from '@unhead/vue';
import { UnheadPlugin } from '@unhead/vue/vue2';
import { Capacitor } from '@capacitor/core';

// others
import App from './App.vue';
import { LoggerPlugin } from '@/plugins/logger';
import { HttpClientPlugin } from '@/plugins/httpClient';
import { SoulodyClientPlugin } from '@/plugins/soulodyClient';
import { init as filtersInit } from '@/plugins/filters';
import { init as directivesInit } from '@/plugins/directives';
import { createI18n } from '@/plugins/i18n';
import router from '@/router';
import { AuthPlugin } from '@/plugins/auth';
import { createStore } from '@/store';
import { GlobalMixinsPlugin } from '@/plugins/global-mixins';
import { default as vuetifyPreset } from './vuetify.options';
import { i18nRoutes } from '@/plugins/i18nRoutes';
import { FlutterPlugin } from '@/plugins/flutter';
import { initSentry } from '@/plugins/sentry';
import { RegionPlugin } from '@/plugins/region';
import { GraphqlPlugin } from '@/plugins/graphql';
import { AssetPlugin } from '@/plugins/asset';
import { EventHubPlugin } from '@/plugins/eventHub';
import { ApiAuthPlugin } from '@/plugins/apiAuth';
import { GlobalComponentsPlugin } from '@/plugins/globalComponents';
import { useCapacitorStorage } from '@/composables/capacitor/useCapacitorStorage.ts';
import { useCapacitorBiometry } from '@/composables/capacitor/useCapacitorBiometry.ts';
import { useCapacitorPushNotifications } from '@/composables/capacitor/useCapacitorPushNotifications.ts';
import { useCapacitorLiveUpdate } from '@/composables/capacitor/useCapacitorLiveUpdate.ts';
import { useCapacitorSocialLogin } from '@/composables/capacitor/useCapacitorSocialLogin.ts';
import { useIsLoggedInRedirect } from '@/composables/useIsLoggedInRedirect';
import '@mdi/font/css/materialdesignicons.css';
import '@/styles/main.scss';
import '@/styles/css-variables.css';

import { createTokenService } from '@/services/token';

(async () => {
  const i18n = createI18n(RegionPlugin.getRegion());
  await createTokenService(RegionPlugin.topDomainEnvObject[RegionPlugin.getRegion()]);

  const head = createHead();

  const gtmCodes = {
    cz: import.meta.env.VITE_GTM_CODE_CZ,
    sk: import.meta.env.VITE_GTM_CODE_SK,
    hu: import.meta.env.VITE_GTM_CODE_HU
  };

  Vue.use(LoggerPlugin);
  Vue.use(Vuex);
  const store = createStore();
  store.subscribe(mutation => {
    useIsLoggedInRedirect(mutation, router);
  });
  initSentry(router);

  Vue.use(Vuelidate);
  Vue.use(Flicking);
  Vue.use(VueTheMask);
  Vue.use(LottieAnimation);
  Vue.use(VCalendar);
  Vue.use(VueEllipseProgress);
  Vue.use(RegionPlugin);

  Vue.use(VueGtm, {
    id: gtmCodes[RegionPlugin.getRegion()],
    defer: true,
    compatibility: true,
    enabled: import.meta.env.VITE_GTM_ENABLED,
    debug: false,
    loadScript: true
  });

  Vue.use(Router);
  Vue.use(i18nRoutes);
  Vue.use(Vuetify);
  Vue.use(GlobalMixinsPlugin);
  Vue.use(GraphqlPlugin);
  Vue.use(HttpClientPlugin);
  Vue.use(SoulodyClientPlugin);
  Vue.use(ApiAuthPlugin, { store });
  Vue.use(FlutterPlugin, { store });
  Vue.use(AuthPlugin, { store });
  Vue.use(VueMeta);
  Vue.use(UnheadPlugin);
  Vue.use(AssetPlugin);
  Vue.use(EventHubPlugin);
  Vue.use(VueScrollTo);
  Vue.use(GlobalComponentsPlugin);

  filtersInit(Vue);
  directivesInit(Vue);

  Vue.config.productionTip = false;
  Vue.prototype.$eventHub = new Vue();

  Vue.prototype.$log('SPA', `vite mode: ${import.meta.env.MODE}`);
  Vue.prototype.$log('SPA', `version: ${import.meta.env.VITE_APP_VERSION}`);
  Vue.prototype.$log('SPA', `init done`, 'S');

  // Service worker — persistent cache for the 34 MB Shen.AI WASM that iOS
  // Safari otherwise re-downloads on every refresh (its disk-cache cap and
  // `immutable`-directive handling are inconsistent above ~25–50 MB).
  // Skip on dev (HMR conflicts) and on Capacitor (native plugin, no WASM).
  if (import.meta.env.PROD && !Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(err => {
      console.warn('[sw] registration failed', err);
    });
  }

  const mountApp = () => {
    new Vue({
      vuetify: new Vuetify(vuetifyPreset),
      router,
      i18n,
      store,
      render: handler => handler(App),
      unhead: head
    }).$mount('#app');
  };

  if (Capacitor.isNativePlatform()) {
    const Storage = useCapacitorStorage();
    if (Storage) {
      const biometryAvailable = await Storage.getValue('biometryAvailable');
      const biometryEnabled = await Storage.getValue('biometryEnabled');
      const { checkBiometry, authenticate } = useCapacitorBiometry();
      if (biometryAvailable !== 'true') {
        const result = await checkBiometry();
        await Storage.setValue('biometryAvailable', `${result.isAvailable}`);
      }

      if (biometryEnabled === 'true') {
        await authenticate();
      }

      const pushNotificationsEnabled = await Storage.getValue('pushNotificationsEnabled');
      const { initPushNotifications } = useCapacitorPushNotifications();
      if (pushNotificationsEnabled !== 'true' && pushNotificationsEnabled !== 'false') {
        const result = await initPushNotifications();
        await Storage.setValue('pushNotificationsEnabled', `${result}`);
      }
    }

    const capacitorSocialLoginApi = useCapacitorSocialLogin();
    await capacitorSocialLoginApi.initialize({
      googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      appleClientId: import.meta.env.VITE_APPLE_CLIENT_ID
    });

    if (import.meta.env.MODE !== 'development') {
      const { ready, sync } = useCapacitorLiveUpdate({
        url: `${import.meta.env.VITE_BFF_BASE_URL}release/latest`,
        apiKey: import.meta.env.VITE_BFF_API_KEY
      });
      await ready();
      sync();
    }
  }

  mountApp();
})();
